import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Edit2, Trash2, Filter, ChevronDown, X, Save, Settings2 } from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../shared/ApiServices/LabServices';
import { TestCatalogSkeleton } from '../../shared/custom-ui/skeleton/Lab';
import useLabPermissions from './useLabPermissions';
import { Select, Tabs, Input } from '../../shared/custom-ui';
import {
    extractLabTestsList, extractLabTestId, extractTestName,
    extractDoctorsList, extractDoctorId, extractDoctorName,
    extractSampleTypesList, extractSampleTypeId,
    extractTestGroupsList, extractLabTestGroupId,
    extractTestParametersList,
    formatCurrency, formatLabDate, parseAmount,
    type LabTest, type ReferenceDoctor, type LabSampleType,
    type LabTestGroup, type LabTestParameter,
} from './labUtils';

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = ['Tests', 'Groups', 'Sample Types', 'Doctors'] as const;
type Tab = typeof TABS[number];

// ─── Simple modal wrapper ─────────────────────────────────────────────────────
const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; width?: string }> = ({
    title, onClose, children, width = 'max-w-xl',
}) => createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
        <div className={`w-full ${width} rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-2xl flex flex-col max-h-[90vh]`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                <h2 className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0">{title}</h2>
                <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] hover:bg-[var(--v2-bg-surface)] transition-colors border-0 bg-transparent">
                    <X className="w-4 h-4" strokeWidth={2} />
                </button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4">{children}</div>
        </div>
    </div>,
    document.body
);

// ─── Field ────────────────────────────────────────────────────────────────────
const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
    <div className="flex flex-col gap-1">
        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);

const inputCls = "w-full rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] px-3 py-2 text-[13px] text-[var(--v2-text-primary)] outline-none transition-colors";

// ─── Confirm Delete Modal ─────────────────────────────────────────────────────
const DeleteModal: React.FC<{ name: string; deleting: boolean; onCancel: () => void; onConfirm: () => void }> =
    ({ name, deleting, onCancel, onConfirm }) => createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
            <div className="w-full max-w-sm rounded-2xl border border-red-200 bg-[var(--v2-bg-card)] shadow-2xl p-6 flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <h3 className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0">Confirm Delete</h3>
                    <p className="text-[12px] text-[var(--v2-text-muted)] m-0">
                        Are you sure you want to delete <strong className="text-[var(--v2-text-primary)]">"{name}"</strong>? This cannot be undone.
                    </p>
                </div>
                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={onCancel} disabled={deleting}
                        className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] disabled:opacity-50 transition-colors">
                        Cancel
                    </button>
                    <button type="button" onClick={onConfirm} disabled={deleting}
                        className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors border-0">
                        {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );

// ═══════════════════════════════════════════════════════════════════════════════
// TESTS TAB
// ═══════════════════════════════════════════════════════════════════════════════

const CATEGORIES = ['Hematology', 'Biochemistry', 'Microbiology', 'Immunology', 'Pathology', 'Radiology', 'Endocrinology', 'Other'];

interface TestForm {
    testName: string; testCode: string; category: string; price: string;
    cgstPct: string; sgstPct: string; igstPct: string;
    sampleType: string; turnaroundHours: string; unit: string; description: string;
}
const emptyTestForm = (): TestForm => ({
    testName: '', testCode: '', category: '', price: '', cgstPct: '6', sgstPct: '6', igstPct: '0',
    sampleType: '', turnaroundHours: '', unit: '', description: '',
});

const TestsTab: React.FC<{ canManage: boolean }> = ({ canManage }) => {
    const [tests, setTests] = useState<LabTest[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [editTest, setEditTest] = useState<LabTest | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<TestForm>(emptyTestForm());
    const [showParams, setShowParams] = useState<LabTest | null>(null);
    const [params, setParams] = useState<LabTestParameter[]>([]);
    const [paramsSaving, setParamsSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await LabServices.getLabTests({ category: catFilter || undefined, search: search || undefined });
            const all = extractLabTestsList(res);
            setTests(all.filter(t => String(t.isActive ?? 'true').toLowerCase() !== 'false'));
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to load tests');
        } finally { setLoading(false); }
    }, [search, catFilter]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(emptyTestForm()); setShowCreate(true); };
    const openEdit = (t: LabTest) => {
        setForm({
            testName: t.testName ?? t.test_name ?? '',
            testCode: t.testCode ?? t.test_code ?? '',
            category: t.category ?? '',
            price: String(t.price ?? ''),
            cgstPct: String(t.cgstPct ?? 6),
            sgstPct: String(t.sgstPct ?? 6),
            igstPct: String(t.igstPct ?? 0),
            sampleType: t.sampleType ?? t.sample_type ?? '',
            turnaroundHours: String(t.turnaroundHours ?? t.turnaround_hours ?? ''),
            unit: t.unit ?? '',
            description: t.description ?? '',
        });
        setEditTest(t);
    };

    const handleSave = async () => {
        if (!form.testName.trim() || !form.testCode.trim() || !form.price) {
            toast.error('Test name, code and price are required'); return;
        }
        setSaving(true);
        try {
            const payload = {
                testName: form.testName.trim(), testCode: form.testCode.trim(),
                category: form.category, price: Number(form.price),
                cgstPct: Number(form.cgstPct), sgstPct: Number(form.sgstPct), igstPct: Number(form.igstPct),
                sampleType: form.sampleType, turnaroundHours: Number(form.turnaroundHours) || undefined,
                unit: form.unit, description: form.description,
            };
            if (editTest) {
                const res: any = await LabServices.updateLabTest(extractLabTestId(editTest), payload);
                toast.success(res?.message ?? 'Test updated successfully');
                setEditTest(null);
            } else {
                const res: any = await LabServices.createLabTest(payload as any);
                toast.success(res?.message ?? 'Test created');
                setShowCreate(false);
            }
            load();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Save failed');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res: any = await LabServices.deleteLabTest(deleteTarget.id);
            // API soft-deletes: "Test deactivated (soft-deleted)"
            toast.success(res?.message ?? 'Test deactivated');
            setDeleteTarget(null);
            load();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Delete failed');
        } finally { setDeleting(false); }
    };

    const openParams = async (test: LabTest) => {
        setShowParams(test);
        try {
            const res = await LabServices.getTestParameters(extractLabTestId(test));
            setParams(extractTestParametersList(res));
        } catch { setParams([]); }
    };

    const saveParams = async () => {
        if (!showParams) return;
        setParamsSaving(true);
        try {
            const res: any = await LabServices.saveTestParameters(extractLabTestId(showParams), params.map(p => ({
                paramName: p.paramName ?? p.param_name ?? '',
                unit: p.unit ?? '',
                referenceRange: p.referenceRange ?? p.reference_range ?? '',
                gender: p.gender,
                ageRange: p.ageRange ?? p.age_range,
                criticalLow: p.criticalLow,
                criticalHigh: p.criticalHigh,
            })));
            toast.success(res?.message ?? 'Parameters saved');
            setShowParams(null);
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Save failed');
        } finally { setParamsSaving(false); }
    };

    const TestForm = (
        <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Test Name" required>
                    <input className={inputCls} value={form.testName} onChange={e => setForm(f => ({ ...f, testName: e.target.value }))} placeholder="Complete Blood Count" />
                </Field>
                <Field label="Test Code" required>
                    <input className={inputCls} value={form.testCode} onChange={e => setForm(f => ({ ...f, testCode: e.target.value }))} placeholder="CBC001" />
                </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Category">
                    <Select
                        value={form.category}
                        onChange={v => setForm(f => ({ ...f, category: v }))}
                        placeholder="— Select —"
                        options={CATEGORIES.map(c => ({ value: c, label: c }))}
                    />
                </Field>
                <Field label="Price (₹)" required>
                    <input type="number" className={inputCls} value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="450.00" />
                </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
                <Field label="CGST %"><input type="number" className={inputCls} value={form.cgstPct} onChange={e => setForm(f => ({ ...f, cgstPct: e.target.value }))} /></Field>
                <Field label="SGST %"><input type="number" className={inputCls} value={form.sgstPct} onChange={e => setForm(f => ({ ...f, sgstPct: e.target.value }))} /></Field>
                <Field label="IGST %"><input type="number" className={inputCls} value={form.igstPct} onChange={e => setForm(f => ({ ...f, igstPct: e.target.value }))} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Sample Type"><input className={inputCls} value={form.sampleType} onChange={e => setForm(f => ({ ...f, sampleType: e.target.value }))} placeholder="EDTA Blood" /></Field>
                <Field label="TAT (hours)"><input type="number" className={inputCls} value={form.turnaroundHours} onChange={e => setForm(f => ({ ...f, turnaroundHours: e.target.value }))} placeholder="12" /></Field>
            </div>
            <Field label="Unit"><input className={inputCls} value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="cells/mcL" /></Field>
            <Field label="Description"><textarea className={`${inputCls} resize-none`} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></Field>
            <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => { setShowCreate(false); setEditTest(null); }}
                    className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] transition-colors">
                    Cancel
                </button>
                <button type="button" onClick={handleSave} disabled={saving}
                    className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 transition-opacity flex items-center gap-1.5 border-0"
                    style={{ background: '#059669' }}>
                    <Save className="w-3.5 h-3.5" strokeWidth={2} />
                    {saving ? 'Saving…' : 'Save Test'}
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
                <Input
                    fullWidth={false}
                    size="small"
                    className="w-64"
                    placeholder="Search tests…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    leftIcon={<Search strokeWidth={2} className="w-full h-full" />}
                    rightIcon={search ? (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            aria-label="Clear search"
                            className="pointer-events-auto flex items-center justify-center w-full h-full cursor-pointer text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] bg-transparent border-0"
                        >
                            <X className="w-full h-full" strokeWidth={2} />
                        </button>
                    ) : undefined}
                />
                <Select
                    className="w-44"
                    size="small"
                    value={catFilter}
                    onChange={setCatFilter}
                    placeholder="All categories"
                    options={CATEGORIES.map(c => ({ value: c, label: c }))}
                />
                {canManage && (
                    <button type="button" onClick={openCreate}
                        className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white border-0 transition-opacity hover:opacity-90"
                        style={{ background: '#059669' }}>
                        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Create Test
                    </button>
                )}
            </div>

            {/* Table */}
            {loading ? <TestCatalogSkeleton /> : (
                <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                    {tests.length === 0 ? (
                        <div className="flex flex-col items-center py-16 gap-3 text-center">
                            <p className="text-[13px] font-bold text-[var(--v2-text-secondary)]">No tests found</p>
                            {canManage && <button type="button" onClick={openCreate}
                                className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white border-0" style={{ background: '#059669' }}>
                                Create first test
                            </button>}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr style={{ background: 'var(--v2-bg-surface)' }}>
                                        {['Test Name', 'Code', 'Category', 'Price', 'TAT', 'Actions'].map(h => (
                                            <th key={h} className="text-left px-4 py-3 font-bold text-[var(--v2-text-muted)] text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tests.map(test => {
                                        const id = extractLabTestId(test);
                                        return (
                                            <tr key={id} className="border-t border-[var(--v2-border-divider)] hover:bg-[var(--v2-bg-surface)] transition-colors">
                                                <td className="px-4 py-3 font-semibold text-[var(--v2-text-primary)] max-w-[200px] truncate">{extractTestName(test)}</td>
                                                <td className="px-4 py-3 font-mono text-[var(--v2-text-muted)]">{test.testCode ?? test.test_code ?? '—'}</td>
                                                <td className="px-4 py-3">
                                                    {test.category && (
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold"
                                                            style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>
                                                            {test.category}
                                                        </span>
                                                    )}
                                                </td>
                                                {/* price is a string from API — parse before formatting */}
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col gap-0">
                                                        <span className="font-bold text-[var(--v2-text-primary)]">
                                                            {formatCurrency(parseAmount(test.price))}
                                                        </span>
                                                        {test.grandTotal && parseAmount(test.grandTotal) > parseAmount(test.price) && (
                                                            <span className="text-[9px] text-[var(--v2-text-muted)]">
                                                                w/ GST: {formatCurrency(parseAmount(test.grandTotal))}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-[var(--v2-text-muted)]">{test.turnaroundHours ?? (test as any).turnaround_hours ? `${test.turnaroundHours ?? (test as any).turnaround_hours}h` : '—'}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1">
                                                        <button type="button" title="Parameters" onClick={() => openParams(test)}
                                                            className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] hover:text-[#059669] hover:border-green-300 transition-all bg-transparent">
                                                            <Settings2 className="w-3.5 h-3.5" strokeWidth={2} />
                                                        </button>
                                                        {canManage && <>
                                                            <button type="button" title="Edit" onClick={() => openEdit(test)}
                                                                className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] transition-all bg-transparent">
                                                                <Edit2 className="w-3.5 h-3.5" strokeWidth={2} />
                                                            </button>
                                                            <button type="button" title="Delete" onClick={() => setDeleteTarget({ id, name: extractTestName(test) })}
                                                                className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border border-transparent text-red-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all bg-transparent">
                                                                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                                            </button>
                                                        </>}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            {(showCreate || editTest) && (
                <Modal title={editTest ? 'Edit Test' : 'Create Lab Test'} onClose={() => { setShowCreate(false); setEditTest(null); }}>
                    {TestForm}
                </Modal>
            )}
            {deleteTarget && (
                <DeleteModal name={deleteTarget.name} deleting={deleting} onCancel={() => setDeleteTarget(null)} onConfirm={handleDelete} />
            )}
            {showParams && (
                <Modal title={`Parameters — ${extractTestName(showParams)}`} onClose={() => setShowParams(null)} width="max-w-2xl">
                    <div className="flex flex-col gap-3">
                        <div className="overflow-x-auto rounded-xl border border-[var(--v2-border-card)]">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr style={{ background: 'var(--v2-bg-surface)' }}>
                                        {['Parameter', 'Unit', 'Reference Range', 'Gender', 'Age Range', 'Critical Low', 'Critical High', ''].map(h => (
                                            <th key={h} className="text-left px-3 py-2.5 font-bold text-[var(--v2-text-muted)] text-[10px] uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {params.map((p, i) => (
                                        <tr key={i} className="border-t border-[var(--v2-border-divider)]">
                                            <td className="px-3 py-2"><input className={`${inputCls} min-w-[120px]`} value={p.paramName ?? p.param_name ?? ''} onChange={e => setParams(ps => ps.map((r, j) => j === i ? { ...r, paramName: e.target.value } : r))} placeholder="Hemoglobin" /></td>
                                            <td className="px-3 py-2"><input className={`${inputCls} w-20`} value={p.unit ?? ''} onChange={e => setParams(ps => ps.map((r, j) => j === i ? { ...r, unit: e.target.value } : r))} placeholder="g/dL" /></td>
                                            <td className="px-3 py-2"><input className={`${inputCls} w-32`} value={p.referenceRange ?? p.reference_range ?? ''} onChange={e => setParams(ps => ps.map((r, j) => j === i ? { ...r, referenceRange: e.target.value } : r))} placeholder="13.8 - 17.2" /></td>
                                            <td className="px-3 py-2">
                                                <Select className="w-24" size="small" value={p.gender ?? 'All'}
                                                    onChange={v => setParams(ps => ps.map((r, j) => j === i ? { ...r, gender: v } : r))}
                                                    options={['All', 'Male', 'Female'].map(g => ({ value: g, label: g }))} />
                                            </td>
                                            <td className="px-3 py-2"><input className={`${inputCls} w-24`} value={p.ageRange ?? p.age_range ?? ''} onChange={e => setParams(ps => ps.map((r, j) => j === i ? { ...r, ageRange: e.target.value } : r))} placeholder="Adult" /></td>
                                            <td className="px-3 py-2"><input className={`${inputCls} w-20`} value={p.criticalLow ?? ''} onChange={e => setParams(ps => ps.map((r, j) => j === i ? { ...r, criticalLow: e.target.value } : r))} placeholder="e.g. 6" /></td>
                                            <td className="px-3 py-2"><input className={`${inputCls} w-20`} value={p.criticalHigh ?? ''} onChange={e => setParams(ps => ps.map((r, j) => j === i ? { ...r, criticalHigh: e.target.value } : r))} placeholder="e.g. 20" /></td>
                                            <td className="px-3 py-2"><button type="button" onClick={() => setParams(ps => ps.filter((_, j) => j !== i))} className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer text-red-400 hover:text-red-600 bg-transparent border-0"><X className="w-3.5 h-3.5" /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button type="button" onClick={() => setParams(ps => [...ps, {}])}
                            className="self-start inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer border border-dashed border-[#059669] text-[#059669] bg-transparent hover:bg-green-50 transition-colors">
                            <Plus className="w-3 h-3" strokeWidth={2.5} /> Add Row
                        </button>
                        <div className="flex gap-2 justify-end">
                            <button type="button" onClick={() => setShowParams(null)} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)] transition-colors">Cancel</button>
                            <button type="button" onClick={saveParams} disabled={paramsSaving}
                                className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0 flex items-center gap-1.5" style={{ background: '#059669' }}>
                                <Save className="w-3.5 h-3.5" strokeWidth={2} />{paramsSaving ? 'Saving…' : 'Save Parameters'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// GROUPS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const GroupsTab: React.FC<{ canManage: boolean }> = ({ canManage }) => {
    const [groups, setGroups] = useState<LabTestGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [allTests, setAllTests] = useState<LabTest[]>([]);
    const [form, setForm] = useState({ groupName: '', groupCode: '', description: '', price: '', tests: [] as string[] });

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [g, t] = await Promise.all([LabServices.getTestGroups(), LabServices.getLabTests()]);
                setGroups(extractTestGroupsList(g));
                setAllTests(extractLabTestsList(t));
            } catch { } finally { setLoading(false); }
        })();
    }, []);

    const handleSave = async () => {
        if (!form.groupName.trim()) { toast.error('Group name is required'); return; }
        setSaving(true);
        try {
            const res: any = await LabServices.createTestGroup({
                groupName: form.groupName,
                groupCode: form.groupCode || undefined,
                description: form.description,
                testIds: form.tests,   // send as testIds (API field)
                price: Number(form.price) || 0,
            });
            toast.success(res?.message ?? 'Group created');
            setShowCreate(false);
            const g = await LabServices.getTestGroups(); setGroups(extractTestGroupsList(g));
        } catch (err: any) { toast.error(err?.message ?? 'Save failed'); } finally { setSaving(false); }
    };

    return (
        <div className="flex flex-col gap-4">
            {canManage && (
                <div className="flex justify-end">
                    <button type="button" onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white border-0" style={{ background: '#059669' }}>
                        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Create Group
                    </button>
                </div>
            )}
            {loading ? <div className="h-32 rounded-2xl" style={{ background: 'var(--v2-border-subtle)' }} /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {groups.map(g => (
                        <div key={extractLabTestGroupId(g)} className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-4 flex flex-col gap-2" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0">{g.groupName ?? g.group_name}</p>
                                    {(g.groupCode ?? g.group_code) && (
                                        <p className="text-[10px] font-mono text-[var(--v2-text-muted)] m-0">{g.groupCode ?? g.group_code}</p>
                                    )}
                                </div>
                                <span className="text-[12px] font-black" style={{ color: '#059669' }}>{formatCurrency(parseAmount(g.price))}</span>
                            </div>
                            {g.description && <p className="text-[11px] text-[var(--v2-text-muted)] m-0">{g.description}</p>}
                            {/* API returns testIds[] */}
                            <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{(g.testIds ?? g.tests ?? []).length} tests</p>
                        </div>
                    ))}
                    {groups.length === 0 && <p className="text-sm text-[var(--v2-text-muted)] col-span-3 py-10 text-center">No groups yet.</p>}
                </div>
            )}
            {showCreate && (
                <Modal title="Create Test Group" onClose={() => setShowCreate(false)}>
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Group Name" required><input className={inputCls} value={form.groupName} onChange={e => setForm(f => ({ ...f, groupName: e.target.value }))} placeholder="Basic Health Checkup" /></Field>
                            <Field label="Group Code"><input className={inputCls} value={form.groupCode} onChange={e => setForm(f => ({ ...f, groupCode: e.target.value }))} placeholder="BHC01 (optional)" /></Field>
                        </div>
                        <Field label="Price (₹)"><input type="number" className={inputCls} value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} /></Field>
                        <Field label="Description"><textarea className={`${inputCls} resize-none`} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></Field>
                        <Field label="Tests (select multiple)">
                            <div className="rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] max-h-40 overflow-y-auto p-2 flex flex-col gap-1">
                                {allTests.map(t => {
                                    const id = extractLabTestId(t);
                                    return (
                                        <label key={id} className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg hover:bg-[var(--v2-bg-card)] transition-colors">
                                            <input type="checkbox" checked={form.tests.includes(id)}
                                                onChange={e => setForm(f => ({ ...f, tests: e.target.checked ? [...f.tests, id] : f.tests.filter(x => x !== id) }))} />
                                            <span className="text-[12px] text-[var(--v2-text-primary)]">{extractTestName(t)}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </Field>
                        <div className="flex gap-2 justify-end">
                            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)]">Cancel</button>
                            <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0" style={{ background: '#059669' }}>
                                {saving ? 'Saving…' : 'Create Group'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SAMPLE TYPES TAB
// ═══════════════════════════════════════════════════════════════════════════════
const SampleTypesTab: React.FC<{ canManage: boolean }> = ({ canManage }) => {
    const [types, setTypes] = useState<LabSampleType[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ sampleTypeName: '', bottleColor: '', department: '', handlingInstructions: '' });

    const load = async () => { setLoading(true); try { setTypes(extractSampleTypesList(await LabServices.listSampleTypes())); } catch { } finally { setLoading(false); } };
    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        if (!form.sampleTypeName.trim()) { toast.error('Name is required'); return; }
        setSaving(true);
        try { await LabServices.createSampleType(form); toast.success('Sample type created'); setShowCreate(false); load(); }
        catch (err: any) { toast.error(err?.message ?? 'Save failed'); } finally { setSaving(false); }
    };

    return (
        <div className="flex flex-col gap-4">
            {canManage && (
                <div className="flex justify-end">
                    <button type="button" onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white border-0" style={{ background: '#059669' }}>
                        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Sample Type
                    </button>
                </div>
            )}
            {loading ? <div className="h-32 rounded-2xl" style={{ background: 'var(--v2-border-subtle)' }} /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {types.map(t => (
                        <div key={extractSampleTypeId(t)} className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-4 flex flex-col gap-1.5" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                            <div className="flex items-center gap-2">
                                {t.bottleColor && <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: t.bottleColor?.toLowerCase() ?? '#999' }} />}
                                <p className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0">{t.sampleTypeName ?? t.sample_type_name}</p>
                            </div>
                            {t.department && <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{t.department}</p>}
                            {/* API stores notes in "description" field */}
                            {(t.description || t.handlingInstructions || t.handling_instructions) && (
                                <p className="text-[10px] text-[var(--v2-text-muted)] m-0 line-clamp-2">
                                    {t.description ?? t.handlingInstructions ?? t.handling_instructions}
                                </p>
                            )}
                        </div>
                    ))}
                    {types.length === 0 && <p className="text-sm text-[var(--v2-text-muted)] col-span-3 py-10 text-center">No sample types yet.</p>}
                </div>
            )}
            {showCreate && (
                <Modal title="Add Sample Type" onClose={() => setShowCreate(false)}>
                    <div className="flex flex-col gap-3">
                        <Field label="Sample Type Name" required><input className={inputCls} value={form.sampleTypeName} onChange={e => setForm(f => ({ ...f, sampleTypeName: e.target.value }))} placeholder="EDTA Blood" /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Bottle Color"><input className={inputCls} value={form.bottleColor} onChange={e => setForm(f => ({ ...f, bottleColor: e.target.value }))} placeholder="Lavender" /></Field>
                            <Field label="Department"><input className={inputCls} value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} placeholder="Hematology" /></Field>
                        </div>
                        <Field label="Handling Instructions"><textarea className={`${inputCls} resize-none`} rows={2} value={form.handlingInstructions} onChange={e => setForm(f => ({ ...f, handlingInstructions: e.target.value }))} /></Field>
                        <div className="flex gap-2 justify-end">
                            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)]">Cancel</button>
                            <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0" style={{ background: '#059669' }}>
                                {saving ? 'Saving…' : 'Add'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DOCTORS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const DoctorsTab: React.FC<{ canManage: boolean }> = ({ canManage }) => {
    const [doctors, setDoctors] = useState<ReferenceDoctor[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [editDoc, setEditDoc] = useState<ReferenceDoctor | null>(null);
    const [saving, setSaving] = useState(false);
    const emptyForm = { doctorName: '', specialization: '', mobile: '', email: '', hospital: '', commissionPct: '0' };
    const [form, setForm] = useState(emptyForm);

    const load = async () => { setLoading(true); try { setDoctors(extractDoctorsList(await LabServices.listReferenceDoctors())); } catch { } finally { setLoading(false); } };
    useEffect(() => { load(); }, []);

    const openEdit = (d: ReferenceDoctor) => {
        setForm({ doctorName: d.doctorName ?? d.doctor_name ?? '', specialization: d.specialization ?? '', mobile: d.mobile ?? '', email: d.email ?? '', hospital: d.hospital ?? '', commissionPct: String(d.commissionPct ?? d.commission_pct ?? 0) });
        setEditDoc(d);
    };

    const handleSave = async () => {
        if (!form.doctorName.trim()) { toast.error('Doctor name is required'); return; }
        setSaving(true);
        try {
            const payload = { ...form, commissionPct: Number(form.commissionPct) };
            if (editDoc) { await LabServices.updateReferenceDoctor(extractDoctorId(editDoc), payload); toast.success('Doctor updated'); setEditDoc(null); }
            else { await LabServices.createReferenceDoctor(payload); toast.success('Doctor created'); setShowCreate(false); }
            load();
        } catch (err: any) { toast.error(err?.message ?? 'Save failed'); } finally { setSaving(false); }
    };

    const DoctorForm = (
        <div className="flex flex-col gap-3">
            <Field label="Doctor Name" required><input className={inputCls} value={form.doctorName} onChange={e => setForm(f => ({ ...f, doctorName: e.target.value }))} placeholder="Dr. Sarah Jenkins" /></Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Specialization"><input className={inputCls} value={form.specialization} onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))} placeholder="Cardiology" /></Field>
                <Field label="Mobile"><input className={inputCls} value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} placeholder="+15550198" /></Field>
            </div>
            <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></Field>
            <Field label="Hospital"><input className={inputCls} value={form.hospital} onChange={e => setForm(f => ({ ...f, hospital: e.target.value }))} /></Field>
            <Field label="Commission %"><input type="number" className={inputCls} value={form.commissionPct} onChange={e => setForm(f => ({ ...f, commissionPct: e.target.value }))} /></Field>
            <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowCreate(false); setEditDoc(null); }} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)] hover:bg-[var(--v2-bg-card)]">Cancel</button>
                <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0" style={{ background: '#059669' }}>
                    {saving ? 'Saving…' : (editDoc ? 'Update' : 'Create')}
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            {canManage && (
                <div className="flex justify-end">
                    <button type="button" onClick={() => { setForm(emptyForm); setShowCreate(true); }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white border-0" style={{ background: '#059669' }}>
                        <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add Doctor
                    </button>
                </div>
            )}
            {loading ? <div className="h-32 rounded-2xl" style={{ background: 'var(--v2-border-subtle)' }} /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {doctors.map(d => (
                        <div key={extractDoctorId(d)} className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-4 flex flex-col gap-2" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0">{extractDoctorName(d)}</p>
                                    {d.specialization && <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{d.specialization}</p>}
                                </div>
                                <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>
                                    {d.commissionPct ?? d.commission_pct ?? 0}% comm.
                                </span>
                            </div>
                            {d.hospital && <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{d.hospital}</p>}
                            {d.mobile && <p className="text-[10px] text-[var(--v2-text-muted)] m-0">{d.mobile}</p>}
                            {canManage && (
                                <button type="button" onClick={() => openEdit(d)}
                                    className="self-start inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] bg-transparent transition-all mt-1">
                                    <Edit2 className="w-3 h-3" strokeWidth={2} /> Edit
                                </button>
                            )}
                        </div>
                    ))}
                    {doctors.length === 0 && <p className="text-sm text-[var(--v2-text-muted)] col-span-3 py-10 text-center">No reference doctors yet.</p>}
                </div>
            )}
            {(showCreate || editDoc) && (
                <Modal title={editDoc ? 'Edit Doctor' : 'Add Reference Doctor'} onClose={() => { setShowCreate(false); setEditDoc(null); }}>
                    {DoctorForm}
                </Modal>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════════════════
const TAB_ITEMS = TABS.map(tab => ({ id: tab, label: tab }));

export const TestCatalog: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('Tests');
    const { canManageCatalog } = useLabPermissions();

    return (
        <div className="v2-animate-fade-in">
            <Tabs
                tabs={TAB_ITEMS}
                activeTab={activeTab}
                setActiveTab={(id) => setActiveTab(id as Tab)}
                variant="pills"
                size="small"
            >
                <div className="flex flex-col gap-4">
                    {activeTab === 'Tests' && <TestsTab canManage={canManageCatalog} />}
                    {activeTab === 'Groups' && <GroupsTab canManage={canManageCatalog} />}
                    {activeTab === 'Sample Types' && <SampleTypesTab canManage={canManageCatalog} />}
                    {activeTab === 'Doctors' && <DoctorsTab canManage={canManageCatalog} />}
                </div>
            </Tabs>
        </div>
    );
};

export default TestCatalog;
